import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RBACContext = createContext({
  accessConfig: null,
  loading: true,
  canAccessPage: () => true,
  canSeeField: () => true,
  refreshAccess: () => {},
});

export const RBACProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [accessConfig, setAccessConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAccess = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setAccessConfig(null);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/rbac/my-access`, { withCredentials: true });
      setAccessConfig(res.data);
    } catch (e) {
      console.error('Failed to fetch RBAC config:', e);
      setAccessConfig({ role: user?.role, full_access: false, pages: {} });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => { fetchAccess(); }, [fetchAccess]);

  const canAccessPage = (pageKey) => {
    if (!accessConfig) return true;
    if (accessConfig.full_access) return true;
    const pageConfig = accessConfig.pages?.[pageKey];
    if (!pageConfig) return true; // Default: accessible if no config set
    return pageConfig.visible !== false;
  };

  const canSeeField = (pageKey, fieldKey) => {
    if (!accessConfig) return true;
    if (accessConfig.full_access) return true;
    const pageConfig = accessConfig.pages?.[pageKey];
    if (!pageConfig) return true;
    const fields = pageConfig.fields || {};
    if (fieldKey in fields) return fields[fieldKey];
    return true; // Default: visible if not explicitly set
  };

  return (
    <RBACContext.Provider value={{
      accessConfig,
      loading,
      canAccessPage,
      canSeeField,
      refreshAccess: fetchAccess,
    }}>
      {children}
    </RBACContext.Provider>
  );
};

export const useRBAC = () => useContext(RBACContext);
